<template lang="pug">
  .product-details.container.mx-auto.px-4.my-8(ref="container")
    .loading(v-if="loading" key="spinner")
      //- TODO: skeleton
      Spinner(:size="10" :thickness="2")
    .details(v-else-if="product" :class="transition" key="details")
      .media(ref="media")
        .placeholder.rounded.rounded-b-none(v-if="!media.length")
          svg
            line(x1="0" y1="100%" x2="100%" y2="0")
            line(x1="0" y1="0" x2="100%" y2="100%")
        .active-media(v-if="activeMedia && mediaWidth")
          ProductImage(
            v-if="activeMedia.type === 'image'"
            :key="`main-${activeMedia.id}`"
            :media="activeMedia"
            :product="product"
            :preferred-width="mediaWidth"
          )
          //- ProductVideo(
          //-   v-else-if="activeMedia.type === 'video'"
          //-   :key="`main-${activeMedia.id}`"
          //-   :media="activeMedia"
          //-   :product="product"
          //-   :play="active"
          //- )
        ul.thumbnails(v-if="media.length > 1")
          li(v-for="(el, m) in media")
            //- :aspect-ratio="9 / 16"
            PreviewImage(
              v-if="el.type === 'image'"
              :key="el.id"
              :media="el"
              :product="product"
              :preferred-width="120"
              :maximized="el.id === activeMedia.id"
              @maximize="data => selectMedia(m)"
            )
            //- PreviewVideo(
            //-   v-else-if="el.type === 'video'"
            //-   :key="el.id"
            //-   :media="el"
            //-   :product="product"
            //-   :aspect-ratio="9 / 16"
            //-   @maximize="maximize"
            //- )
      .info.zoom-container
        h2.name {{ name }}<br>
        .nr Artikel-Nr. {{ nr }}
        .properties
          ProductProperty(
            v-for="property in properties"
            :valid="valid"
            :key="`${product.id}-${property.id}`"
            :property="property"
            :active-option="selectedOptions[property.id]"
            :sellable-articles="sellableArticles"
            :valid-articles="validArticles"
            :manage-stock="product.manage_stock"
            @select="selectPropertyOption"
          )
        .price
          nobr(:class="{ strikethrough: salePrice }") {{ price }}
          nobr.sale(v-if="salePrice") {{ salePrice }}
        .buttons(:class="{ active: validPropertySelection && validArticles && validArticles.length === 1 }")
          .left
            .custom-number-input
              button.decrease(@click="qty = Math.max(1, qty - 1)")
                span.m-auto.text-2xl.font-thin &minus;
              input(type="number" name="custom-input-number" v-model="qty")
              button.increase(@click="qty = Math.min(maxQty, qty + 1)")
                span.m-auto.text-2xl.font-thin +
          .right
            button.button(v-if="valid" @click="addToCart") In den Warenkorb
            //- button.wish-list.button.is-link.has-icon.block.w-full
              font-awesome-icon(:icon="{ prefix: 'far', iconName: 'heart' }")
              | Auf den Wunschzettel
        .description(v-if="description" v-html="description")
    .not-found(v-else key="not-found")
      Error.error(:code="404" message="Produkt konnte nicht gefunden werden")
</template>

<script>
import uniqid from 'uniqid';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import { get, debounce, cloneDeep } from '@/helpers';
import { moneyFormat } from '@/filters';

import Error from '@/components/Error';
import ProductImage from '@/extensions/shop/components/ProductImage';
import PreviewImage from '@/extensions/shop/components/PreviewImage';
import ProductProperty from '@/extensions/shop/components/ProductProperty';

export default {
  name: 'ProductDetailPage',
  data: () => ({
    loading: false,
    product: undefined,
    m: 0, // active media key
    mediaWidth: undefined,
    selectedOptions: {}, // key is property id
    transition: {
      // transition classes
      'slide-fade-enter': true,
      'slide-fade-enter-active': false,
      'slide-fade-leave-active': false,
      'slide-fade-leave-to': false
    },
    qty: 1
  }),
  components: {
    Error,
    ProductImage,
    PreviewImage,
    ProductProperty
  },
  watch: {
    qty(value) {
      if (value < 1) this.qty = 1;
      else if (value > this.maxQty) this.qty = this.maxQty;
    }
  },
  computed: {
    nr() {
      return this.product.nr;
    },
    name() {
      let name = this.product.name;

      if (this.validArticles && this.validArticles.length && this.validArticles[0].name) {
        name = this.validArticles[0].name;
      }

      return name;
    },
    description() {
      let description = get(this.product, 'description');
      // if (!description) description = this.productProperties.find(el => el.key === 'buchinfo');
      // if (!description) description = '';
      // if (typeof description == 'object') return description.value;

      if (this.validArticles && this.validArticles.length && this.validArticles[0].description) {
        description = this.validArticles[0].description;
      }

      return description;
    },
    media() {
      let media = this.product.media;

      if (
        Object.keys(this.selectedOptions).length &&
        this.validArticles &&
        this.validArticles.length &&
        this.validArticles[0].media &&
        this.validArticles[0].media.length
      ) {
        media = this.validArticles[0].media;
      }

      media = media.filter(el => {
        return get(el, 'settings.show.detail', true) === true;
      });

      return media.sort((a, b) => a.position - b.position);
    },
    properties() {
      let properties = [];

      const normalizePropertyOptions = el => {
        const propType = el.property.propType;
        let options = el.values.map(el => {
          const key = propType.value_field;
          const value = get(el, key);

          let valueAlt;
          const keyAlt = propType.value_field_alt;
          if (keyAlt) valueAlt = get(el, keyAlt);

          return {
            id: el.id,
            image: el.image,
            value,
            valueAlt,
            key
          };
        });
        return options;
      };

      if (this.product.propertyPivots && this.product.propertyPivots.length) {
        this.product.propertyPivots.forEach(el => {
          const id = el.property.id;
          const label = el.property.name;
          const visible = el.property.visible_on_product_listing;
          const type = el.property.propType.key;

          // const propType = el.property.propType;
          const articleRequired = el.property.article_required;

          // let key = el.key
          const options = normalizePropertyOptions(el);
          let property = {
            id,
            type,
            label,
            options,
            articleRequired,
            visible
          };

          // if (el.key.toLowerCase() === 'länge') {
          //   property.label = 'length';
          // }
          properties.push(property);
        });
      }

      return properties.filter(property => {
        return property.visible !== false || property.options.length > 1;
      });
    },
    valid() {
      // check if this product is valid
      // by required stuff is missing
      // - price
      // ...
      return !!this.product.price;
    },
    price() {
      if (!this.product.price) return null;
      return this.getPrice();
    },
    salePrice() {
      if (!this.product.salePrice) return null;
      return this.getPrice('salePrice');
    },
    maxQty() {
      let maxQty = Infinity;
      if (this.validArticles && this.validArticles.length === 1 && this.validArticles[0].qty_sum !== null) {
        maxQty = this.validArticles[0].qty_sum;
      }
      return maxQty;
    },
    activeMedia() {
      const m = Math.min(this.media.length - 1, this.m);
      return this.media[m];
    },
    configurableArticleProperties() {
      return this.properties.filter(property => {
        return property.options && property.options.length > 1 && property.articleRequired;
      });
    },
    configurableProductProperties() {
      return this.properties.filter(property => {
        return property.options && property.options.length > 1 && property.articleRequired === false;
      });
    },
    nonConfigurableProperties() {
      return this.properties.filter(property => property.options && property.options.length <= 1);
    },
    validPropertySelection() {
      let valid = false;
      const pendingProperties = this.properties.filter(property => {
        return property.options.length > 1 && !this.selectedOptions[property.id];
      });
      if (!pendingProperties.length) valid = true;
      return valid;
    },
    sellableArticles() {
      let articles = this.product.articles;

      // pay attention to stock
      const manageStock = this.product.manage_stock;
      if (manageStock && this.product.articles && this.product.articles.length) {
        articles = this.product.articles.filter(article => article.qty_sum > 0);
      }
      return articles;
    },
    validArticles() {
      let articles = this.sellableArticles;
      if (this.sellableArticles && this.sellableArticles.length) {
        articles = this.sellableArticles.filter(article => {
          let match = true;

          // check qty in cart
          // qty in cart must be smaller
          // than article.qty_sum (if manageStock)
          if (this.product.manage_stock) {
            let cartQty = 0;
            this.cart.articlePivots.forEach(articlePivot => {
              if (articlePivot.article.id === article.id) {
                cartQty += articlePivot.qty;
              }
            });
            if (article.qty_sum <= cartQty) match = false;
          }

          // all property pivots must match
          if (match) {
            article.propertyPivots.forEach(el => {
              const property = el.property;
              const value = el.value;
              if (this.selectedOptions[property.id]) {
                const option = this.selectedOptions[property.id];
                if (value[option.key] !== option.value) match = false;
              }
            });
          }
          return match;
        });
      }
      return articles;
    },
    ...mapGetters({
      customer: 'shop/customer',
      defaultCustomerGroup: 'shop/defaultCustomerGroup',
      defaultTax: 'shop/defaultTax',
      cart: 'shop/cart'
    })
  },
  methods: {
    getPrice(key = 'price') {
      const code =
        get(this.customer, 'customerGroup.currency.iso4217') ||
        get(this.defaultCustomerGroup, 'customerGroup.currency.iso4217') ||
        'EUR';

      let string = moneyFormat(this.product[key].value, code);

      // get all prices from related articles
      // define range

      let range = {
        from: this.product[key].value,
        to: this.product[key].value
      };

      if (this.validArticles && this.validArticles.length) {
        this.validArticles.forEach(article => {
          if (article[key]) {
            range.from = Math.min(range.from, article[key].value);
            range.to = Math.max(range.from, article[key].value);
          }
        });

        if (range.from < range.to) {
          string = 'ab ' + moneyFormat(range.from, code);
          // string = moneyFormat(range.from, code) + ' - ' + moneyFormat(range.to, code);
        }
      }

      return string;
    },
    calculateMediaWidth() {
      if (this.$refs.media) {
        const style = getComputedStyle(this.$refs.media);
        this.mediaWidth = this.$refs.media.clientWidth;
        this.mediaWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      }
    },
    selectMedia(m) {
      this.m = m;
    },
    selectPropertyOption({ property, option }) {
      if (this.selectedOptions[property.id] && this.selectedOptions[property.id].id === option.id) {
        this.$delete(this.selectedOptions, property.id);
      } else {
        const value = { label: property.label, articleRequired: property.articleRequired, ...option };
        this.$set(this.selectedOptions, property.id, value);

        // check if there are validArticles
        // if not delete all selectedOptions
        // that are not available in combination
        // with the latest selection

        if (this.sellableArticles.length) {
          let attempts = 0;
          while (!this.validArticles.length && attempts < 1) {
            ++attempts;

            let match = false;
            if (!match) {
              this.properties.forEach(prop => {
                if (
                  prop.articleRequired &&
                  prop.options.length &&
                  this.selectedOptions[prop.id] &&
                  prop.id !== property.id
                ) {
                  // find sellable article with selected options
                  // without the current opt
                  // if there are some, delete this prop from selectedOptions

                  const articles = this.sellableArticles.filter(article => {
                    // all property pivots must match
                    let match = true;
                    article.propertyPivots.forEach(el => {
                      const property = el.property;
                      const value = el.value;
                      if (prop.id !== property.id && this.selectedOptions[property.id]) {
                        const option = this.selectedOptions[property.id];
                        if (value[option.key] !== option.value) match = false;
                      }
                    });
                    return match;
                  });

                  if (articles.length) {
                    this.$delete(this.selectedOptions, prop.id);
                    match = true;
                  }
                }
              });
            }
          }
        }
      }
    },
    addToCart() {
      if (!this.validArticles || this.validArticles.length > 1) {
        const errorMessage = {
          id: uniqid(),
          title: 'Fehler',
          text: 'Es gibt ein technisches Problem.<br>Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
          type: 'error',
          autoclose: 8000
        };
        this.addMessage(errorMessage);
      } else {
        let article = this.validArticles[0];
        article.product = cloneDeep(this.product);
        delete article.product.articles;

        let productPropertyValues = [];
        Object.keys(this.selectedOptions).map(key => {
          if (!this.selectedOptions[key].articleRequired) {
            productPropertyValues.push(this.selectedOptions[key].id);
          }
        });

        this.addArticleToCart({ article, productPropertyValues, qty: parseInt(this.qty) });

        const properties = Object.keys(this.selectedOptions)
          .map(key => `${this.selectedOptions[key].label}: ${this.selectedOptions[key].value}`)
          .join('<br>');

        const text = `
          <p>Der folgende Artikel wurde Ihrem Warenkorb hinzugefügt</p>
          <blockquote class="mt-2 pl-3 border-l-2 border-white border-opacity-50">
            <h4 class="text-xl leading-none mb-1">${this.name}</h4>
            <p class="text-xs mb-2 text-white text-opacity-75">#${this.nr}</p>
            <p>${properties}</p>
            <p>Preis: ${this.salePrice || this.price}</p>
          </blockquote>
        `;

        const message = {
          id: uniqid(),
          text,
          type: 'success',
          autoclose: 4000
        };
        this.addMessage(message);

        this.selectedOptions = {};
        this.qty = 1;
      }
    },
    ...mapMutations({
      unsetPage: 'unsetPage',
      setProduct: 'shop/setProduct'
    }),
    ...mapActions({
      addMessage: 'message/add',
      addArticleToCart: 'shop/addArticleToCart',
      fetchProduct: 'shop/fetchProduct'
    })
  },
  mounted() {
    this.loading = true;

    this.fetchProduct({ slug: this.$route.params.product })
      .then(product => {
        this.product = product;

        // NOTE: needed for breadcrumb
        this.setProduct(product);

        setTimeout(this.calculateMediaWidth, 0);

        this.transition['slide-fade-enter-active'] = true;
        setTimeout(() => (this.transition['slide-fade-enter'] = false), 0);
        setTimeout(() => (this.transition['slide-fade-enter-active'] = false), 300);
      })
      .finally(() => {
        this.loading = false;
      });

    this.debouncedCalculateMediaWidth = debounce(this.calculateMediaWidth, 100);
    window.addEventListener('resize', this.debouncedCalculateMediaWidth);
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      setTimeout(() => {
        // NOTE: reset page state after animation finished
        vm.unsetPage();
      }, 350);
    });
  },
  beforeRouteLeave(to, from, next) {
    this.transition['slide-fade-leave-active'] = true;
    this.transition['slide-fade-leave-to'] = true;
    this.setProduct(undefined);
    next();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.debouncedCalculateMediaWidth);
  }
};
</script>

<style lang="sass" scoped>
@import "~@/assets/sass/variables"

.loading > section
  min-height: 140px
.thumbnails
  display: flex
  flex-wrap: wrap
  justify-content: center
  margin: 2rem 0
  gap: 1rem
  a
    transition: box-shadow .15s ease
    display: block

.media
  margin-bottom: 3rem
.name
  font-size: 2rem
  line-height: 1.1
  margin-bottom: 1rem
  small
    font-size: 1rem
    top: -0.3rem
    position: relative
.nr
  margin-bottom: 3rem
.properties
  margin-bottom: 3rem
  .property
    margin-bottom: 1rem
.price
  text-align: right
  font-size: 2rem
  margin-bottom: 3rem

.details
  position: relative
  ::v-deep .drift-zoom-pane
    @apply rounded overflow-hidden
    position: absolute
    left: 0
    top: 0
    width: 100%
    height: 80vh

.buttons
  @apply flex
  justify-content: space-between
  opacity: .3
  pointer-events: none
  transition: opacity .15s ease
  margin-bottom: 3rem
  &.active
    pointer-events: initial
    opacity: 1
  .left
    display: flex
    flex-grow: 1
    justify-content: flex-end
    padding-right: 1rem

.custom-number-input
  @apply w-32 flex flex-row rounded-lg relative
  button
    @apply bg-white text-gray-600 h-full w-20 cursor-pointer outline-none
    height: 2.5rem
    &:hover,
    &:focus
      @apply text-gray-700 bg-gray-100
    &.decrease
      @apply rounded-l
    &.increase
      @apply rounded-r
  input
    @apply outline-none rounded-none border-none text-center w-full bg-white font-semibold flex items-center text-gray-700
    @apply shadow-none #{! important}
    height: 2.5rem
    &:hover,
    &:focus
      @apply text-black

@screen md
  .product-details
    display: flex
    flex-direction: column
    flex-grow: 1
    & > div
      width: 100%
    .loading
      display: flex
      flex-grow: 1
      width: 100%
    .details
      display: grid
      grid-template-rows: auto
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr)
      grid-gap: 6rem
  .info
    position: relative
    ::v-deep .drift-zoom-pane
      @apply rounded overflow-hidden
      position: absolute
      left: -5rem
      top: 0
      width: calc(100% + 5rem)
      height: 60vh

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button
  -webkit-appearance: none
  margin: 0

.custom-number-input input:focus
  outline: none !important

.custom-number-input button:focus
  outline: none !important


.placeholder
  @apply rounded bg-white
  padding-bottom: 100%
  position: relative
  display: none
  @screen md
    display: block
  svg
    width: calc(100% - 2rem)
    height: calc(100% - 2rem)
    position: absolute
    top: 1rem
    left: 1rem
    line
      stroke: rgba($red, .3)
      stroke-width: 1.02

.strikethrough
  font-size: 0.8em
  opacity: 0.6
  color: $black
  &:before
    border-color: theme('colors.gray.700')

.price
  position: relative
.price nobr + nobr.sale
  margin-left: 1rem
  color: theme('colors.red.600')
  position: relative
  z-index: 1
  &:before
    content: ''
    position: absolute
    // background-color: theme('colors.red.100')
    background-color: #e6ff15
    width: calc(100% + 8px)
    height: calc(100% + 4px)
    left: -4px
    top: -7px
    z-index: -1
    transform: rotate(-9deg) skew(-2deg, 0deg)
</style>
